let activeDialog;
let activeRequest;
let returnFocus;

const emailSelector = 'input[name="tx_powermail_pi1[field][email]"]';
const genericErrorMessage = 'Die Anmeldung konnte gerade nicht gesendet werden. Bitte versuchen Sie es später erneut.';

function configureNewsletterForms(root = document) {
    root.querySelectorAll(emailSelector).forEach((email) => {
        email.type = 'email';
        email.autocomplete = 'email';
        email.inputMode = 'email';
        email.spellcheck = false;
        email.maxLength = 254;
    });
}

function createDialogShell(content) {
    const container = document.createElement('div');
    container.className = 'fancybox-container fancybox-is-open';
    container.setAttribute('role', 'dialog');
    container.innerHTML = '<div class="fancybox-bg"></div><div class="fancybox-inner"><div class="fancybox-stage"><div class="fancybox-slide fancybox-slide--html fancybox-slide--current"></div></div></div>';
    const title = content.querySelector('.title, h1, h2, h3');
    if (title) {
        title.id ||= `${content.id || 'newsletter-dialog'}-title`;
        container.setAttribute('aria-labelledby', title.id);
    }
    const slide = container.querySelector('.fancybox-slide');
    content.classList.add('fancybox-content');
    // `.popup` is hidden by the shipped newsletter stylesheet; make the
    // selected panel explicit when it is moved into our vanilla dialog shell.
    content.style.display = 'block';
    slide.append(content);
    return container;
}

function focusFirst(dialog) {
    const target = dialog.querySelector('input:not([type="hidden"]):not([disabled]), button:not([disabled]), a[href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    target?.focus({preventScroll: true});
}

function setSubmitting(form, submitting) {
    if (submitting) {
        form.setAttribute('aria-busy', 'true');
    } else {
        form.removeAttribute('aria-busy');
    }
    form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((submit) => {
        submit.disabled = submitting;
    });
}

function clearSubmissionError(form) {
    form.querySelectorAll('.newsletter-form-error, .powermail_message_error').forEach((error) => error.remove());
    form.querySelectorAll('.powermail_field_error').forEach((field) => field.classList.remove('powermail_field_error'));
}

function showSubmissionError(form, message = genericErrorMessage) {
    clearSubmissionError(form);
    const error = document.createElement('p');
    error.className = 'newsletter-form-error';
    error.setAttribute('role', 'alert');
    error.setAttribute('tabindex', '-1');
    error.textContent = message;
    form.prepend(error);
    error.focus({preventScroll: true});
}

function formUid(form) {
    return form.querySelector('input[name="tx_powermail_pi1[mail][form]"]')?.value || '';
}

function replaceInvalidForm(form, responseDocument) {
    const uid = formUid(form);
    const selector = uid ? `form.powermail_form_${CSS.escape(uid)}` : 'form.powermail_form';
    const responseForm = responseDocument.querySelector(selector);
    const currentWrapper = form.closest('.tx-powermail');
    const responseWrapper = responseForm?.closest('.tx-powermail');
    if (!responseForm || !currentWrapper || !responseWrapper) return false;

    currentWrapper.replaceChildren(...Array.from(responseWrapper.childNodes, (node) => document.importNode(node, true)));
    configureNewsletterForms(currentWrapper);
    const errorTitle = currentWrapper.querySelector('.powermail-message-title');
    if (errorTitle) errorTitle.textContent = 'Anmeldung nicht möglich';
    currentWrapper.querySelectorAll('.powermail-message-field').forEach((field) => field.remove());
    const focusTarget = currentWrapper.querySelector(`${emailSelector}, input:not([type="hidden"]), button, [tabindex]:not([tabindex="-1"])`);
    focusTarget?.focus({preventScroll: true});
    return true;
}

function responseConfirmsSubmission(form, responseDocument) {
    const uid = formUid(form);
    if (!uid) return false;
    return Boolean(responseDocument.querySelector(`[data-powermail-form="${CSS.escape(uid)}"]`));
}

async function submitNewsletterForm(form) {
    if (form.dataset.newsletterSubmitting === 'true') return;
    const email = form.querySelector(emailSelector);
    if (email) email.value = email.value.trim();
    if (!form.reportValidity()) return;

    clearSubmissionError(form);
    form.dataset.newsletterSubmitting = 'true';
    setSubmitting(form, true);
    const controller = new AbortController();
    activeRequest = {controller, form};

    try {
        const response = await fetch(form.action, {
            method: form.method || 'POST',
            body: new FormData(form),
            credentials: 'same-origin',
            headers: {'Accept': 'text/html'},
            signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Newsletter request failed with HTTP ${response.status}`);

        const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (replaceInvalidForm(form, responseDocument)) return;
        if (!responseConfirmsSubmission(form, responseDocument)) {
            throw new Error('Newsletter response did not contain a Powermail success marker.');
        }

        activeRequest = null;
        closeNewsletterDialog();
        openNewsletterDialog(document.querySelector('.subscribe-holder .success-popup'));
    } catch (error) {
        if (error.name !== 'AbortError' && form.isConnected) {
            window.console.error('Newsletter registration failed.', error);
            showSubmissionError(form);
        }
    } finally {
        if (activeRequest?.form === form) activeRequest = null;
        delete form.dataset.newsletterSubmitting;
        if (form.isConnected) setSubmitting(form, false);
    }
}

export function closeNewsletterDialog() {
    if (!activeDialog) return;
    activeRequest?.controller.abort();
    activeRequest = null;
    const focusTarget = returnFocus;
    returnFocus = null;
    const content = activeDialog.querySelector('.popup');
    const holder = document.querySelector('.subscribe-holder');
    if (content && holder) {
        content.classList.remove('fancybox-content');
        content.style.display = 'none';
        holder.append(content);
    }
    activeDialog.remove();
    activeDialog = null;
    document.documentElement.classList.remove('fancybox-enabled');
    document.body.classList.remove('newsletter-dialog-open', 'fancybox-active', 'compensate-for-scrollbar');
    if (focusTarget?.isConnected) {
        const opener = focusTarget.closest('.subscribe-opener');
        // The legacy stylesheet transitions `visibility` while the panel is
        // closing. Disable that transition for one frame so the opener is
        // focusable synchronously when the dialog node is removed.
        opener?.style.setProperty('transition', 'none');
        focusTarget.focus({preventScroll: true});
        requestAnimationFrame(() => opener?.style.removeProperty('transition'));
    }
}

export function openNewsletterDialog(content, returnFocusTarget = document.activeElement) {
    if (!content || activeDialog) return;
    returnFocus = returnFocusTarget;
    configureNewsletterForms(content);
    activeDialog = createDialogShell(content);
    document.body.classList.add('newsletter-dialog-open');
    document.body.append(activeDialog);
    activeDialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-fancybox-close], .fancybox-bg')) {
            event.preventDefault();
            closeNewsletterDialog();
        }
    });
    focusFirst(activeDialog);
}

function handleKeydown(event) {
    if (!activeDialog) return;
    if (event.key === 'Escape') {
        event.preventDefault();
        closeNewsletterDialog();
    }
}

function handleSubmit(event) {
    const form = event.target.closest?.('.subscribe-popup form');
    if (!form) return;
    event.preventDefault();
    // Powermail can optionally register its own AJAX handler on the form. The
    // popup owns this submission so it can keep server errors inside the modal.
    event.stopImmediatePropagation();
    submitNewsletterForm(form);
}

export function initNewsletterDialog() {
    const holder = document.querySelector('.subscribe-holder');
    configureNewsletterForms(document);
    if (!holder || holder.dataset.newsletterDialog === 'ready') return;
    holder.dataset.newsletterDialog = 'ready';
    holder.querySelectorAll('.popup').forEach((popup) => { popup.style.display = 'none'; });
    document.addEventListener('submit', handleSubmit, true);
    document.addEventListener('keydown', handleKeydown);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNewsletterDialog, {once: true});
} else {
    initNewsletterDialog();
}
